import React from 'react';
import { Box } from 'pounceV2';
import type {
  EditorWillMount,
  EditorDidMount,
  monaco,
  MonacoEditorProps,
} from 'react-monaco-editor';
import type { SchemasSettings } from 'monaco-yaml';
import useForceUpdate from 'Hooks/useForceUpdate';
import { useTheme } from '@mui/material';
import { TypographyTokens } from 'pounceV2/src/theme/typography-tokens';
import pantherTheme from './themes/panther-dark';
import pantherThemeLight from './themes/panther-light';
import useEditorDecorations from './hooks/useEditorDecorations';
import useEditorConfigV3 from './hooks/useEditorConfigV3';
import useEditorLanguage from './hooks/useEditorLanguage';
import useEditorCommands from './hooks/useEditorCommands';
import useEditorCompletions from './hooks/useEditorCompletions';
import useEditorResize from './hooks/useEditorResize';
import EditorWrapperV3 from './EditorWrapperV3';
import useEditorYamlValidation from './hooks/useEditorYamlValidation';
import { languageConfiguration, pantherflowConfig } from './languages/pantherflow';

// Lazy-load the Monaco editor.
const MonacoEditor = React.lazy(
  () =>
    import(
      /* webpackChunkName: "react-monaco-editor" */ /* webpackPrefetch: true */ 'react-monaco-editor'
    ),
);

export type YamlValidationOptions = {
  schema: SchemasSettings['schema'];
  docUri: string;
};

export type Completion = {
  value: string;
  type: string;
  label?: string;
  detail?: string;
  filterText?: string;
  kind?: monaco.languages.CompletionItemKind;
  documentation?: string;
  /**
   * If provided, this completion will only be shown when the current line's
   * indentation matches this exact number of characters.
   * For example, set to 4 to only show this completion when indented by 4 spaces.
   */
  requiredIndentation?: number;
};

export type LineChanges = monaco.editor.ILineChange[];

export type EditorCommand = {
  keyBindings: number;
  handler: monaco.editor.ICommandHandler;
};

export type EditorLineError = {
  line: number;
  reason: string;
};

export type EditorProps = Pick<MonacoEditorProps, 'value' | 'onChange'> & {
  ariaLabel?: string;
  completions?: Completion[];
  minHeight?: number;
  maxHeight?: number;
  placeholder?: string;
  readOnly?: boolean;
  language?: 'yaml' | 'json' | 'sql' | 'python' | 'pantherflow' | 'starlark';
  fontSize?: keyof TypographyTokens['fontSizes'];
  hideLineNumbers?: boolean;
  commands?: (monacoInstance: Pick<typeof monaco, 'KeyCode' | 'KeyMod'>) => EditorCommand[];
  /**
   * **initialScrollLine** should be provided if we want the editor
   * to scroll on that line when starting
   * @default 1
   */
  initialScrollLine?: number;
  disableAutocomplete?: boolean;
  /**
   * **lineErrors** should be an array of EditorLineErrors to
   * display errors in specific lines editors
   */
  lineErrors?: EditorLineError[];
  lineChanges?: LineChanges;
  /**
   * Json schema that will be used to provide validation and autocomplete functionality.
   * Should be used only with "yaml" language!
   */
  yamlValidationOptions?: YamlValidationOptions;
  /**
   * Controls whether completions should be computed based on words in the document. Defaults to true.
   */
  wordBasedSuggestions?: boolean;
  hideScrollbar?: boolean;
  /**
   * Controls whether the editor should be focused when it mounts. Defaults to false.
   */
  autoFocus?: boolean;
  /** Enable Monaco's code lens feature. Defaults to false. */
  codeLens?: boolean;
  getEditorCb?: (editor: monaco.editor.IStandaloneCodeEditor) => void;
};

const EditorV3: React.FC<EditorProps> = ({
  completions = [],
  value,
  onChange,
  disableAutocomplete,
  ariaLabel,
  readOnly = false,
  minHeight = 170,
  maxHeight,
  placeholder,
  language,
  fontSize = 'sm',
  hideLineNumbers = false,
  commands = () => [],
  initialScrollLine = 1,
  lineErrors,
  lineChanges,
  yamlValidationOptions,
  wordBasedSuggestions = true,
  hideScrollbar = false,
  autoFocus = false,
  codeLens = false,
  getEditorCb,
}) => {
  const muiTheme = useTheme();
  const isLightMode = muiTheme.palette.mode === 'light';
  const editorTheme = isLightMode ? pantherThemeLight : pantherTheme;
  const forceUpdate = useForceUpdate();
  const monacoInstanceRef = React.useRef<typeof monaco>(null);
  const monacoEditorRef = React.useRef<monaco.editor.IStandaloneCodeEditor>(null);

  const handleEditorWillMount = React.useCallback<EditorWillMount>(
    monacoInstance => {
      monacoInstance.languages.register({ id: 'pantherflow' });
      monacoInstance.languages.setMonarchTokensProvider('pantherflow', pantherflowConfig);
      monacoInstance.languages.setLanguageConfiguration('pantherflow', languageConfiguration);
      monacoInstance.editor.defineTheme('panther', editorTheme);
    },
    [editorTheme],
  );

  const handleEditorMount = React.useCallback<EditorDidMount>((editor, monacoInstance) => {
    monacoInstanceRef.current = monacoInstance;
    monacoEditorRef.current = editor;
    if (autoFocus) {
      editor.focus();
    }

    // @ts-ignore
    if (window.Cypress) {
      // @ts-ignore
      window.cypress_monaco_editor = editor;
    }

    monacoEditorRef.current.revealLine(initialScrollLine);
    forceUpdate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const monacoInstance = monacoInstanceRef.current;
  const editor = monacoEditorRef.current;

  React.useEffect(() => {
    if (editor && getEditorCb) {
      getEditorCb(editor);
    }
  }, [editor, getEditorCb]);

  // We need to remeasure the fonts when they load
  React.useLayoutEffect(() => {
    document.fonts.ready.then(() => {
      if (monacoInstance?.editor) {
        monacoInstance.editor.remeasureFonts();
      }
    });
  }, [monacoInstance?.editor]);

  const editorConfig = useEditorConfigV3({
    ariaLabel,
    codeLens,
    readOnly,
    fontSize,
    hideLineNumbers,
    hasLineErrors: Boolean(lineErrors),
    wordBasedSuggestions,
    hideScrollbar,
  });

  useEditorLanguage({ monacoInstance, language, disableAutocomplete });
  useEditorCompletions({ monacoInstance, language, completions, wordBasedSuggestions });
  useEditorCommands({ monacoInstance, editor, commands });
  useEditorResize({ editor, minHeight, maxHeight });
  useEditorDecorations({ editor, lineErrors, lineChanges });
  useEditorYamlValidation({ editor, monacoInstance, yamlValidationOptions, language, value });

  return (
    <EditorWrapperV3 placeholder={placeholder} value={value} minHeight={minHeight}>
      <Box
        border="1px solid"
        borderColor={muiTheme.palette.componentTokens.jsonTree.color.stroke.editor.default}
        borderRadius="6px"
      >
        <MonacoEditor
          key={muiTheme.palette.mode}
          options={editorConfig}
          value={value}
          onChange={onChange}
          theme="panther"
          language={language === 'starlark' ? 'python' : language}
          editorWillMount={handleEditorWillMount}
          editorDidMount={handleEditorMount}
        />
      </Box>
    </EditorWrapperV3>
  );
};

export default React.memo(EditorV3);
